import { useState, useEffect } from 'react';
import { UserPlus, Trash2, Copy, Check, Eye, EyeOff, Pencil } from 'lucide-react';
import { apiFetch } from '../api/client';
import { getUser } from '../api/client';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  company_name: string | null;
  created_at: string | null;
}

interface ScopeOption {
  db_id: number;
  db_name: string;
  company_name: string;
}

interface DbEntry {
  id: number;
  name: string;
  db_id: number;
  active?: boolean;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function ScopeSelect({ value, onChange, databases, scopes, adminLabel = 'All databases (admin)', isAdmin }: {
  value: string;
  onChange: (v: string) => void;
  databases: DbEntry[];
  scopes: ScopeOption[];
  adminLabel?: string;
  isAdmin: boolean;
}) {
  if (isAdmin) {
    return (
      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-400 bg-gray-50 select-none">
        {adminLabel}
      </div>
    );
  }
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500 bg-white"
    >
      {databases.map(db => {
        const subs = scopes.filter(s => s.db_id === db.db_id && s.company_name !== db.name);
        return (
          <optgroup key={db.id} label={db.name}>
            <option value={db.name}>{subs.length > 0 ? `${db.name} — full access` : db.name}</option>
            {subs.map(s => (
              <option key={s.company_name} value={s.company_name}>{s.company_name} only</option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

export function UserManagement() {
  const currentUserId = getUser()?.id;

  const [users, setUsers]         = useState<User[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [scopes, setScopes]       = useState<ScopeOption[]>([]);
  const [databases, setDatabases] = useState<DbEntry[]>([]);

  // Create form
  const [showCreate, setShowCreate]   = useState(false);
  const [createName, setCreateName]   = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPw, setCreatePw]       = useState(generatePassword);
  const [createRole, setCreateRole]   = useState<'client' | 'admin'>('client');
  const [createScope, setCreateScope] = useState('');
  const [createShowPw, setCreateShowPw] = useState(false);
  const [createCopied, setCreateCopied] = useState(false);
  const [creating, setCreating]       = useState(false);

  // Edit form
  const [editUser, setEditUser]       = useState<User | null>(null);
  const [editName, setEditName]       = useState('');
  const [editEmail, setEditEmail]     = useState('');
  const [editRole, setEditRole]       = useState<'client' | 'admin'>('client');
  const [editScope, setEditScope]     = useState('');
  const [editResetPw, setEditResetPw] = useState(false);
  const [editPw, setEditPw]           = useState('');
  const [editShowPw, setEditShowPw]   = useState(false);
  const [editCopied, setEditCopied]   = useState(false);
  const [saving, setSaving]           = useState(false);

  const [deleting, setDeleting] = useState<number | null>(null);

  const loadUsers = () => {
    setLoading(true);
    apiFetch<User[]>('/auth/users')
      .then(data => setUsers(data ?? []))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
    Promise.all([
      apiFetch<ScopeOption[]>('/admin/scopes'),
      apiFetch<DbEntry[]>('/admin/databases'),
    ]).then(([s, d]) => {
      const activeDbs = (d ?? []).filter(db => db.active !== false);
      setScopes(s ?? []);
      setDatabases(activeDbs);
      const firstScope = s?.[0]?.company_name ?? '';
      setCreateScope(firstScope);
    }).catch(() => {});
  }, []);

  const openEdit = (user: User) => {
    setEditUser(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditRole(user.role as 'client' | 'admin');
    setEditScope(user.company_name ?? (scopes[0]?.company_name ?? ''));
    setEditResetPw(false);
    setEditPw('');
    setEditShowPw(false);
    setEditCopied(false);
    setError('');
    setSuccess('');
    setShowCreate(false);
  };

  const closeEdit = () => { setEditUser(null); setEditResetPw(false); };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      await apiFetch('/auth/users', {
        method: 'POST',
        body: JSON.stringify({
          name: createName,
          email: createEmail,
          password: createPw,
          role: createRole,
          company_name: createRole === 'admin' ? null : createScope,
        }),
      });
      setSuccess(`User ${createEmail} created. Share the temporary password with them.`);
      setShowCreate(false);
      setCreateName(''); setCreateEmail('');
      setCreatePw(generatePassword()); setCreateRole('client');
      setCreateScope(scopes[0]?.company_name ?? '');
      loadUsers();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setError('');
    setSaving(true);
    try {
      const body: Record<string, any> = {
        name:         editName,
        email:        editEmail,
        role:         editRole,
        company_name: editRole === 'admin' ? null : editScope,
      };
      if (editResetPw && editPw) body.password = editPw;

      await apiFetch(`/auth/users/${editUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setSuccess(`${editName} updated.`);
      closeEdit();
      loadUsers();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (!confirm(`Delete user ${user.email}? This cannot be undone.`)) return;
    setDeleting(user.id);
    try {
      await apiFetch(`/auth/users/${user.id}`, { method: 'DELETE' });
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const copyToClipboard = (text: string, setCopied: (v: boolean) => void) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const PasswordRow = ({ pw, setPw, show, setShow, copied, setCopied }: {
    pw: string; setPw: (v: string) => void;
    show: boolean; setShow: (v: boolean) => void;
    copied: boolean; setCopied: (v: boolean) => void;
  }) => (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <input
          type={show ? 'text' : 'password'}
          value={pw}
          onChange={e => setPw(e.target.value)}
          required
          minLength={8}
          className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg text-sm font-mono text-gray-900 focus:outline-none focus:border-blue-500"
        />
        <button type="button" onClick={() => setShow(!show)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      <button type="button" onClick={() => copyToClipboard(pw, setCopied)}
        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" title="Copy">
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
      </button>
      <button type="button" onClick={() => setPw(generatePassword())}
        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs text-gray-600 whitespace-nowrap">
        Regenerate
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-900">User Management</h2>
          <p className="text-gray-500 text-sm mt-1">Manage client access to monitoring data</p>
        </div>
        {!showCreate && !editUser && (
          <button
            onClick={() => { setShowCreate(true); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <UserPlus className="w-4 h-4" />
            Add User
          </button>
        )}
      </div>

      {error   && <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="px-4 py-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-gray-900 mb-5">New User</h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Full Name</label>
                <input type="text" required value={createName} onChange={e => setCreateName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Email</label>
                <input type="email" required value={createEmail} onChange={e => setCreateEmail(e.target.value)}
                  placeholder="jane@client.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Role</label>
                <select value={createRole} onChange={e => setCreateRole(e.target.value as 'client' | 'admin')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500 bg-white">
                  <option value="client">End User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Database Access</label>
                <ScopeSelect value={createScope} onChange={setCreateScope}
                  databases={databases} scopes={scopes} isAdmin={createRole === 'admin'} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Temporary Password</label>
                <PasswordRow pw={createPw} setPw={setCreatePw}
                  show={createShowPw} setShow={setCreateShowPw}
                  copied={createCopied} setCopied={setCreateCopied} />
                <p className="text-xs text-gray-400 mt-1">Copy this before saving — it won't be shown again.</p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={creating}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-60">
                {creating ? 'Creating…' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Form */}
      {editUser && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-gray-900">Edit User — <span className="text-blue-600">{editUser.email}</span></h3>
          </div>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Full Name</label>
                <input type="text" required value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Email</label>
                <input type="email" required value={editEmail} onChange={e => setEditEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Role</label>
                <select value={editRole}
                  onChange={e => setEditRole(e.target.value as 'client' | 'admin')}
                  disabled={editUser.id === currentUserId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="client">End User</option>
                  <option value="admin">Admin</option>
                </select>
                {editUser.id === currentUserId && (
                  <p className="text-xs text-gray-400 mt-1">Cannot change your own role</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Database Access</label>
                <ScopeSelect value={editScope} onChange={setEditScope}
                  databases={databases} scopes={scopes} isAdmin={editRole === 'admin'}
                  adminLabel="All databases (admin)" />
              </div>
            </div>

            {/* Password reset — opt-in */}
            <div className="border-t border-gray-100 pt-4">
              {!editResetPw ? (
                <button type="button" onClick={() => { setEditResetPw(true); setEditPw(generatePassword()); }}
                  className="text-sm text-blue-600 hover:text-blue-800 underline-offset-2 hover:underline">
                  Reset password…
                </button>
              ) : (
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">New Password</label>
                  <PasswordRow pw={editPw} setPw={setEditPw}
                    show={editShowPw} setShow={setEditShowPw}
                    copied={editCopied} setCopied={setEditCopied} />
                  <p className="text-xs text-gray-400">Copy before saving — it won't be shown again.</p>
                  <button type="button" onClick={() => { setEditResetPw(false); setEditPw(''); }}
                    className="text-xs text-gray-400 hover:text-gray-600">
                    Cancel password reset
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-60">
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" onClick={closeEdit}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No users yet. Add one above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Name</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Email</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Database Access</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Role</th>
                <th className="text-left px-5 py-3 text-gray-600 font-medium">Created</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}
                  className={`border-b border-gray-50 transition-colors ${editUser?.id === user.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-5 py-3 text-gray-900 font-medium">{user.name}</td>
                  <td className="px-5 py-3 text-gray-600">{user.email}</td>
                  <td className="px-5 py-3">
                    {user.role === 'admin' ? (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">All</span>
                    ) : user.company_name ? (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {user.company_name}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs">No scope</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-400">
                    {user.created_at ? (() => {
                      let s = user.created_at.replace(' ', 'T');
                      if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
                      return new Date(s).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
                    })() : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => editUser?.id === user.id ? closeEdit() : openEdit(user)}
                        className={`p-1.5 rounded-lg transition-colors group ${editUser?.id === user.id ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                        title="Edit user"
                      >
                        <Pencil className={`w-4 h-4 ${editUser?.id === user.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'}`} />
                      </button>
                      {user.id !== currentUserId && user.role !== 'admin' && (
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={deleting === user.id}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group disabled:opacity-40"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
